import { useState } from 'react'
import { useEditorStore } from '@/store/editorStore'
import type {
  HeroProps, TextProps, CtaProps, FeaturesProps, TestimonialsProps,
  PricingProps, LogosProps, StatsProps, QuoteProps, CardsProps,
  ProductCatalogProps,
  ImageProps, GalleryProps, SpacerProps, VideoProps, BlockSettings,
  ContactFormProps, EmbedProps, HtmlProps, ColumnsProps, GridProps,
} from '@brix/types'
import {
  TextField, TextareaField, SelectField, NumberField, ColorField,
  ToggleField, ImageField, ItemsEditor, Divider,
} from './fields'
import { RichTextEditor } from './RichTextEditor'

// ─── Settings (shared by all blocks) ─────────────────────────────────────────

function SettingsEditor({ settings, onChange }: { settings: BlockSettings; onChange: (s: Partial<BlockSettings>) => void }) {
  return (
    <div className="space-y-3">
      <ColorField label="Background color" value={settings.backgroundColor ?? '#ffffff'} onChange={v => onChange({ backgroundColor: v })} />
      <ColorField label="Text color" value={settings.textColor ?? ''} onChange={v => onChange({ textColor: v })} />
      <ImageField label="Background image" value={settings.backgroundImage ?? ''} onChange={v => onChange({ backgroundImage: v })} />
      <Divider label="Padding" />
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Top" value={settings.paddingTop ?? 0} onChange={v => onChange({ paddingTop: v })} min={0} />
        <NumberField label="Bottom" value={settings.paddingBottom ?? 0} onChange={v => onChange({ paddingBottom: v })} min={0} />
        <NumberField label="Left" value={settings.paddingLeft ?? 0} onChange={v => onChange({ paddingLeft: v })} min={0} />
        <NumberField label="Right" value={settings.paddingRight ?? 0} onChange={v => onChange({ paddingRight: v })} min={0} />
      </div>
      <Divider label="Navigation" />
      <TextField
        label="Section ID (anchor)"
        value={settings.anchorId ?? ''}
        onChange={v => onChange({ anchorId: v || undefined })}
        placeholder="ej. contacto, precios, servicios"
      />
    </div>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroEditor({ props, onChange }: { props: HeroProps; onChange: (p: Partial<HeroProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline} onChange={v => onChange({ headline: v })} />
      <TextareaField label="Subheadline" value={props.subheadline ?? ''} rows={2} onChange={v => onChange({ subheadline: v })} />
      <Divider label="Primary CTA" />
      <TextField label="Button text" value={props.ctaText ?? ''} onChange={v => onChange({ ctaText: v })} />
      <TextField label="Button URL" value={props.ctaUrl ?? ''} onChange={v => onChange({ ctaUrl: v })} />
      <Divider label="Secondary CTA" />
      <TextField label="Button text" value={props.ctaSecondaryText ?? ''} onChange={v => onChange({ ctaSecondaryText: v })} />
      <TextField label="Button URL" value={props.ctaSecondaryUrl ?? ''} onChange={v => onChange({ ctaSecondaryUrl: v })} />
      <Divider label="Background" />
      <ImageField label="Background image" value={props.backgroundImage ?? ''} onChange={v => onChange({ backgroundImage: v })} folder="heroes" />
      <NumberField label="Overlay opacity %" value={props.overlayOpacity ?? 40} onChange={v => onChange({ overlayOpacity: v })} min={0} max={100} />
      <Divider label="Layout" />
      <SelectField label="Alignment" value={props.alignment} onChange={v => onChange({ alignment: v })}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
      <SelectField label="Layout" value={props.layout} onChange={v => onChange({ layout: v })}
        options={[{ value: 'full-height', label: 'Full height' }, { value: 'half', label: 'Half height' }, { value: 'split', label: 'Split (side image)' }]} />
      <SelectField label="Font style" value={props.fontStyle ?? 'sans'} onChange={v => onChange({ fontStyle: v })}
        options={[{ value: 'sans', label: 'Sans-serif' }, { value: 'serif', label: 'Serif (Playfair)' }]} />
      {props.layout === 'split' && (
        <ImageField
          label="Side image (required for Split)"
          value={props.imageUrl ?? ''}
          onChange={v => onChange({ imageUrl: v })}
          folder="heroes"
        />
      )}
    </div>
  )
}

// ─── Text ─────────────────────────────────────────────────────────────────────

function TextEditor({ props, onChange }: { props: TextProps; onChange: (p: Partial<TextProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextareaField label="HTML content" value={props.content} rows={10} mono onChange={v => onChange({ content: v })} />
      <SelectField label="Alignment" value={props.alignment} onChange={v => onChange({ alignment: v })}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }, { value: 'justify', label: 'Justify' }]} />
    </div>
  )
}

// ─── CTA ──────────────────────────────────────────────────────────────────────

function CtaEditor({ props, onChange }: { props: CtaProps; onChange: (p: Partial<CtaProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline} onChange={v => onChange({ headline: v })} />
      <TextareaField label="Description" value={props.description ?? ''} rows={3} onChange={v => onChange({ description: v })} />
      <Divider label="Primary button" />
      <TextField label="Text" value={props.primaryText} onChange={v => onChange({ primaryText: v })} />
      <TextField label="URL" value={props.primaryUrl} onChange={v => onChange({ primaryUrl: v })} />
      <Divider label="Secondary button" />
      <TextField label="Text" value={props.secondaryText ?? ''} onChange={v => onChange({ secondaryText: v })} />
      <TextField label="URL" value={props.secondaryUrl ?? ''} onChange={v => onChange({ secondaryUrl: v })} />
      <Divider />
      <SelectField label="Alignment" value={props.alignment} onChange={v => onChange({ alignment: v })}
        options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
    </div>
  )
}

// ─── Features ─────────────────────────────────────────────────────────────────

type FeatureItem = FeaturesProps['items'][0]

function FeaturesEditor({ props, onChange }: { props: FeaturesProps; onChange: (p: Partial<FeaturesProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <TextareaField label="Subheadline" value={props.subheadline ?? ''} rows={2} onChange={v => onChange({ subheadline: v })} />
      <Divider />
      <SelectField label="Columns" value={String(props.columns) as never} onChange={v => onChange({ columns: Number(v) as FeaturesProps['columns'] })}
        options={[{ value: '2', label: '2 columns' }, { value: '3', label: '3 columns' }, { value: '4', label: '4 columns' }]} />
      <SelectField label="Layout" value={props.layout} onChange={v => onChange({ layout: v })}
        options={[{ value: 'card', label: 'Cards' }, { value: 'list', label: 'List' }, { value: 'icon-top', label: 'Icon top' }, { value: 'editorial', label: 'Editorial' }]} />
      <Divider />
      <ItemsEditor<FeatureItem>
        label="Items"
        items={props.items}
        onChange={items => onChange({ items })}
        defaultItem={{ title: 'New feature', description: '' }}
        renderItem={(item, patch) => (
          <>
            <TextField label="Icon (emoji)" value={item.icon ?? ''} onChange={v => patch({ icon: v })} placeholder="🚀" />
            <TextField label="Title" value={item.title} onChange={v => patch({ title: v })} />
            <TextareaField label="Description" value={item.description} rows={3} onChange={v => patch({ description: v })} />
          </>
        )}
      />
    </div>
  )
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

type TestimonialItem = TestimonialsProps['items'][0]

function TestimonialsEditor({ props, onChange }: { props: TestimonialsProps; onChange: (p: Partial<TestimonialsProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <SelectField label="Layout" value={props.layout} onChange={v => onChange({ layout: v })}
        options={[{ value: 'grid', label: 'Grid' }, { value: 'carousel', label: 'Carousel' }]} />
      <Divider />
      <ItemsEditor<TestimonialItem>
        label="Testimonials"
        items={props.items}
        onChange={items => onChange({ items })}
        defaultItem={{ quote: '', author: '' }}
        renderItem={(item, patch) => (
          <>
            <TextareaField label="Quote" value={item.quote} rows={3} onChange={v => patch({ quote: v })} />
            <TextField label="Author" value={item.author} onChange={v => patch({ author: v })} />
            <TextField label="Role" value={item.role ?? ''} onChange={v => patch({ role: v })} />
            <ImageField label="Avatar" value={item.avatar ?? ''} onChange={v => patch({ avatar: v })} folder="avatars" />
          </>
        )}
      />
    </div>
  )
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

type PricingTier = PricingProps['tiers'][0]

function PricingEditor({ props, onChange }: { props: PricingProps; onChange: (p: Partial<PricingProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <Divider />
      <ItemsEditor<PricingTier>
        label="Tiers"
        items={props.tiers}
        onChange={tiers => onChange({ tiers })}
        defaultItem={{ name: 'Plan', price: '$0', features: [], ctaText: 'Get started', ctaUrl: '#' }}
        renderItem={(tier, patch) => (
          <>
            <TextField label="Name" value={tier.name} onChange={v => patch({ name: v })} />
            <TextField label="Price" value={tier.price} onChange={v => patch({ price: v })} />
            <TextField label="Period" value={tier.period ?? ''} onChange={v => patch({ period: v })} placeholder="/ month" />
            <TextareaField label="Description" value={tier.description ?? ''} rows={2} onChange={v => patch({ description: v })} />
            <TextareaField label="Features (one per line)" rows={5}
              value={tier.features.join('\n')}
              onChange={v => patch({ features: v.split('\n').filter(Boolean) })} />
            <TextField label="CTA text" value={tier.ctaText ?? ''} onChange={v => patch({ ctaText: v })} />
            <TextField label="CTA URL" value={tier.ctaUrl ?? ''} onChange={v => patch({ ctaUrl: v })} />
            <ToggleField label="Highlighted" value={tier.highlighted ?? false} onChange={v => patch({ highlighted: v })} />
          </>
        )}
      />
    </div>
  )
}

// ─── Logos ────────────────────────────────────────────────────────────────────

type LogoItem = LogosProps['logos'][0]

function LogosEditor({ props, onChange }: { props: LogosProps; onChange: (p: Partial<LogosProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <Divider />
      <ItemsEditor<LogoItem>
        label="Logos"
        items={props.logos}
        onChange={logos => onChange({ logos })}
        defaultItem={{ name: 'Company' }}
        renderItem={(logo, patch) => (
          <>
            <TextField label="Company name" value={logo.name} onChange={v => patch({ name: v })} />
            <ImageField label="Logo image" value={logo.src ?? ''} onChange={v => patch({ src: v })} folder="logos" />
          </>
        )}
      />
    </div>
  )
}

// ─── Stats ────────────────────────────────────────────────────────────────────

type StatItem = StatsProps['items'][0]

function StatsEditor({ props, onChange }: { props: StatsProps; onChange: (p: Partial<StatsProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <Divider />
      <ItemsEditor<StatItem>
        label="Stats"
        items={props.items}
        onChange={items => onChange({ items })}
        defaultItem={{ value: '0', label: 'Metric' }}
        renderItem={(stat, patch) => (
          <>
            <TextField label="Value" value={stat.value} onChange={v => patch({ value: v })} placeholder="10k+" />
            <TextField label="Label" value={stat.label} onChange={v => patch({ label: v })} />
            <TextField label="Description" value={stat.description ?? ''} onChange={v => patch({ description: v })} />
          </>
        )}
      />
    </div>
  )
}

// ─── Quote ────────────────────────────────────────────────────────────────────

function QuoteEditor({ props, onChange }: { props: QuoteProps; onChange: (p: Partial<QuoteProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextareaField label="Quote text" value={props.quote} rows={4} onChange={v => onChange({ quote: v })} />
      <TextField label="Attribution" value={props.attribution ?? ''} onChange={v => onChange({ attribution: v })} />
      <TextField label="Role / context" value={props.role ?? ''} onChange={v => onChange({ role: v })} />
      <SelectField label="Size" value={props.size ?? 'md'} onChange={v => onChange({ size: v })}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
      <ColorField label="Accent color" value={props.accentColor ?? '#c9a96e'} onChange={v => onChange({ accentColor: v })} />
    </div>
  )
}

// ─── Cards ────────────────────────────────────────────────────────────────────

type CardItem = CardsProps['items'][0]

function CardsEditor({ props, onChange }: { props: CardsProps; onChange: (p: Partial<CardsProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Label (small uppercase)" value={props.label ?? ''} onChange={v => onChange({ label: v })} />
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <TextField label="View all text" value={props.viewAllText ?? ''} onChange={v => onChange({ viewAllText: v })} />
      <TextField label="View all URL" value={props.viewAllUrl ?? ''} onChange={v => onChange({ viewAllUrl: v })} />
      <Divider />
      <SelectField label="Columns" value={String(props.columns ?? 3) as never} onChange={v => onChange({ columns: Number(v) as CardsProps['columns'] })}
        options={[{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }, { value: '5', label: '5' }]} />
      <SelectField label="Layout" value={props.layout ?? 'below'} onChange={v => onChange({ layout: v as CardsProps['layout'] })}
        options={[{ value: 'below', label: 'Text below image' }, { value: 'overlay', label: 'Text over image' }]} />
      <Divider />
      <ItemsEditor<CardItem>
        label="Cards"
        items={props.items}
        onChange={items => onChange({ items })}
        defaultItem={{ image: '', title: 'Card title', description: '' }}
        renderItem={(card, patch) => (
          <>
            <ImageField label="Image" value={card.image} onChange={v => patch({ image: v })} folder="cards" />
            <TextField label="Category label" value={card.label ?? ''} onChange={v => patch({ label: v })} />
            <TextField label="Title" value={card.title} onChange={v => patch({ title: v })} />
            <TextField label="Subtitle (author)" value={card.subtitle ?? ''} onChange={v => patch({ subtitle: v })} />
            <TextareaField label="Synopsis" value={card.description ?? ''} rows={3} onChange={v => patch({ description: v })} />
            <TextField label="Link URL" value={card.url ?? ''} onChange={v => patch({ url: v })} placeholder="/catalogo/titulo or #" />
          </>
        )}
      />
    </div>
  )
}

// ─── Product Catalog (Pro plan) ────────────────────────────────────────────────

type ProductItem = ProductCatalogProps['items'][0]

function ProductCatalogEditor({ props, onChange }: { props: ProductCatalogProps; onChange: (p: Partial<ProductCatalogProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} />
      <TextField label="Subheadline" value={props.subheadline ?? ''} onChange={v => onChange({ subheadline: v })} />
      <SelectField label="Columns" value={String(props.columns ?? 3) as never} onChange={v => onChange({ columns: Number(v) as ProductCatalogProps['columns'] })}
        options={[{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }]} />
      <TextField label="Buy button text" value={props.buyButtonText ?? ''} onChange={v => onChange({ buyButtonText: v })} placeholder="Comprar" />
      <TextField label="Sold out text" value={props.soldOutText ?? ''} onChange={v => onChange({ soldOutText: v })} placeholder="Agotado" />
      <Divider />
      <ItemsEditor<ProductItem>
        label="Products"
        items={props.items}
        onChange={items => onChange({ items })}
        defaultItem={{ image: '', name: 'Product name', price: '$0', inStock: true }}
        renderItem={(item, patch) => (
          <>
            <ImageField label="Image" value={item.image} onChange={v => patch({ image: v })} folder="products" />
            <TextField label="Name" value={item.name} onChange={v => patch({ name: v })} />
            <TextField label="Price (display text)" value={item.price} onChange={v => patch({ price: v })} placeholder="$280 / 500g" />
            <TextareaField label="Description" value={item.description ?? ''} rows={2} onChange={v => patch({ description: v })} />
            <ToggleField label="In stock" value={item.inStock} onChange={v => patch({ inStock: v })} />
            <TextField
              label="Checkout URL"
              value={item.checkoutUrl ?? ''}
              onChange={v => patch({ checkoutUrl: v })}
              placeholder="https://buy.stripe.com/... (Stripe Payment Link)"
            />
          </>
        )}
      />
      <p className="text-xs text-gray-400">
        No cart, no inventory tracking — "In stock" is a manual switch per product, and the checkout URL sends
        the customer to an external page (a Stripe Payment Link works well) to actually pay.
      </p>
    </div>
  )
}

// ─── Image ────────────────────────────────────────────────────────────────────

function ImageEditor({ props, onChange }: { props: ImageProps; onChange: (p: Partial<ImageProps>) => void }) {
  return (
    <div className="space-y-3">
      <ImageField label="Image" value={props.src} onChange={v => onChange({ src: v })} folder="images" />
      <TextField label="Alt text" value={props.alt} onChange={v => onChange({ alt: v })} />
      <TextField label="Caption" value={props.caption ?? ''} onChange={v => onChange({ caption: v })} />
      <TextField label="Link URL" value={props.href ?? ''} onChange={v => onChange({ href: v })} />
      <SelectField label="Aspect ratio" value={props.aspectRatio ?? '16/9'} onChange={v => onChange({ aspectRatio: v })}
        options={[{ value: '16/9', label: '16:9' }, { value: '4/3', label: '4:3' }, { value: '3/2', label: '3:2' }, { value: '1/1', label: 'Square' }, { value: 'auto', label: 'Auto' }]} />
      <SelectField label="Object fit" value={props.objectFit ?? 'cover'} onChange={v => onChange({ objectFit: v })}
        options={[{ value: 'cover', label: 'Cover' }, { value: 'contain', label: 'Contain' }]} />
    </div>
  )
}

// ─── Gallery ──────────────────────────────────────────────────────────────────

type GalleryImage = GalleryProps['images'][0]

function GalleryEditor({ props, onChange }: { props: GalleryProps; onChange: (p: Partial<GalleryProps>) => void }) {
  return (
    <div className="space-y-3">
      <SelectField label="Columns" value={String(props.columns) as never} onChange={v => onChange({ columns: Number(v) as GalleryProps['columns'] })}
        options={[{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }]} />
      <SelectField label="Gap" value={props.gap ?? 'md'} onChange={v => onChange({ gap: v as GalleryProps['gap'] })}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
      <ToggleField label="Lightbox" value={props.lightbox ?? true} onChange={v => onChange({ lightbox: v })} />
      <Divider />
      <ItemsEditor<GalleryImage>
        label="Images"
        items={props.images}
        onChange={images => onChange({ images })}
        defaultItem={{ src: '', alt: '' }}
        renderItem={(img, patch) => (
          <>
            <ImageField label="Image" value={img.src} onChange={v => patch({ src: v })} folder="gallery" />
            <TextField label="Alt text" value={img.alt} onChange={v => patch({ alt: v })} />
            <TextField label="Caption" value={img.caption ?? ''} onChange={v => patch({ caption: v })} />
          </>
        )}
      />
    </div>
  )
}

// ─── Spacer ───────────────────────────────────────────────────────────────────

function SpacerEditor({ props, onChange }: { props: SpacerProps; onChange: (p: Partial<SpacerProps>) => void }) {
  return <NumberField label="Height (px)" value={props.height} onChange={v => onChange({ height: v })} min={0} max={400} />
}

// ─── Video ────────────────────────────────────────────────────────────────────

function VideoEditor({ props, onChange }: { props: VideoProps; onChange: (p: Partial<VideoProps>) => void }) {
  return (
    <div className="space-y-3">
      <ImageField label="Video URL or file" value={props.src} onChange={v => onChange({ src: v })} folder="videos" />
      <ImageField label="Poster image" value={props.poster ?? ''} onChange={v => onChange({ poster: v })} folder="images" />
      <ToggleField label="Autoplay" value={props.autoplay ?? false} onChange={v => onChange({ autoplay: v })} />
      <ToggleField label="Loop" value={props.loop ?? false} onChange={v => onChange({ loop: v })} />
      <ToggleField label="Muted" value={props.muted ?? false} onChange={v => onChange({ muted: v })} />
      <ToggleField label="Show controls" value={props.controls ?? true} onChange={v => onChange({ controls: v })} />
    </div>
  )
}

// ─── Contact Form ─────────────────────────────────────────────────────────────

function ContactFormEditor({ props, onChange }: { props: ContactFormProps; onChange: (p: Partial<ContactFormProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} placeholder="Let's talk about your project" />
      <TextareaField label="Subheadline" value={props.subheadline ?? ''} onChange={v => onChange({ subheadline: v })} />
      <TextField label="Button text" value={props.submitText ?? ''} onChange={v => onChange({ submitText: v })} placeholder="Send message" />
      <TextareaField label="Success message" value={props.successMessage ?? ''} onChange={v => onChange({ successMessage: v })} />
    </div>
  )
}

// ─── Embed ────────────────────────────────────────────────────────────────────

function EmbedEditor({ props, onChange }: { props: EmbedProps; onChange: (p: Partial<EmbedProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextareaField
        label="Embed code"
        value={props.code ?? ''}
        onChange={v => onChange({ code: v })}
        placeholder="<iframe src='https://...' ...></iframe>"
      />
      <SelectField
        label="Aspect ratio"
        value={props.aspectRatio ?? '16/9'}
        onChange={v => onChange({ aspectRatio: v as EmbedProps['aspectRatio'] })}
        options={[
          { value: '16/9', label: 'Widescreen (16:9)' },
          { value: '4/3', label: 'Standard (4:3)' },
          { value: '1/1', label: 'Square (1:1)' },
        ]}
      />
    </div>
  )
}

// ─── HTML ─────────────────────────────────────────────────────────────────────

function HtmlEditor({ props, onChange }: { props: HtmlProps; onChange: (p: Partial<HtmlProps>) => void }) {
  return (
    <TextareaField
      label="HTML code"
      value={props.code ?? ''}
      onChange={v => onChange({ code: v })}
      placeholder="<div>Custom HTML goes here</div>"
    />
  )
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const COLUMN_PRESETS = [
  { label: '50 / 50', widths: [50, 50] },
  { label: '33 / 67', widths: [33, 67] },
  { label: '67 / 33', widths: [67, 33] },
  { label: '33 / 33 / 33', widths: [33, 33, 34] },
  { label: '25 / 50 / 25', widths: [25, 50, 25] },
]

function ColumnsEditor({ props, onChange }: { props: ColumnsProps; onChange: (p: Partial<ColumnsProps>) => void }) {
  const widths = props.widths?.length ? props.widths : [50, 50]
  const items = props.items ?? widths.map(() => ({ content: '' }))

  const setItem = (i: number, content: string) => {
    const next = [...items]
    next[i] = { content }
    onChange({ items: next })
  }

  const applyPreset = (w: number[]) => {
    const next = w.map((_, i) => items[i] ?? { content: '' })
    onChange({ widths: w, items: next })
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1.5 text-xs font-medium text-gray-700">Layout</p>
        <div className="grid grid-cols-2 gap-1.5">
          {COLUMN_PRESETS.map(p => (
            <button key={p.label} type="button"
              onClick={() => applyPreset(p.widths)}
              className={['rounded-lg border px-2 py-1.5 text-xs font-medium transition-all',
                JSON.stringify(widths) === JSON.stringify(p.widths)
                  ? 'border-violet-500 bg-violet-50 text-violet-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300',
              ].join(' ')}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <SelectField label="Gap" value={props.gap ?? 'md'} onChange={v => onChange({ gap: v as ColumnsProps['gap'] })}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
      <Divider />
      {widths.map((_, i) => (
        <div key={i}>
          <p className="mb-1 text-xs font-semibold text-gray-700">Column {i + 1} content</p>
          <RichTextEditor value={items[i]?.content ?? ''} onChange={v => setItem(i, v)} placeholder={`Column ${i + 1}…`} />
        </div>
      ))}
    </div>
  )
}

// ─── Grid ─────────────────────────────────────────────────────────────────────

function GridEditor({ props, onChange }: { props: GridProps; onChange: (p: Partial<GridProps>) => void }) {
  const columns = props.columns ?? 3
  const items = props.items ?? Array.from({ length: columns }, () => ({ content: '' }))

  const setItem = (i: number, content: string) => {
    const next = [...items]
    next[i] = { content }
    while (next.length < columns) next.push({ content: '' })
    onChange({ items: next })
  }

  const setColumns = (n: number) => {
    const next = Array.from({ length: n }, (_, i) => items[i] ?? { content: '' })
    onChange({ columns: n, items: next })
  }

  return (
    <div className="space-y-4">
      <NumberField label="Columns" value={columns} onChange={setColumns} min={1} max={4} />
      <SelectField label="Gap" value={props.gap ?? 'md'} onChange={v => onChange({ gap: v as GridProps['gap'] })}
        options={[{ value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
      <ToggleField label="Responsive (stack on mobile)" value={props.responsive ?? true} onChange={v => onChange({ responsive: v })} />
      <Divider />
      {Array.from({ length: columns }).map((_, i) => (
        <div key={i}>
          <p className="mb-1 text-xs font-semibold text-gray-700">Cell {i + 1} content</p>
          <RichTextEditor value={items[i]?.content ?? ''} onChange={v => setItem(i, v)} placeholder={`Cell ${i + 1}…`} />
        </div>
      ))}
    </div>
  )
}

// ─── Blog Feed ────────────────────────────────────────────────────────────────

interface BlogFeedProps { headline?: string; postsCount: number; layout: 'grid' | 'list'; showExcerpt?: boolean; showCover?: boolean }

function BlogFeedEditor({ props, onChange }: { props: BlogFeedProps; onChange: (p: Partial<BlogFeedProps>) => void }) {
  return (
    <div className="space-y-3">
      <TextField label="Headline" value={props.headline ?? ''} onChange={v => onChange({ headline: v })} placeholder="Latest posts" />
      <NumberField label="Number of posts" value={props.postsCount ?? 6} onChange={v => onChange({ postsCount: v })} min={1} max={20} />
      <SelectField
        label="Layout"
        value={props.layout ?? 'grid'}
        onChange={v => onChange({ layout: v as 'grid' | 'list' })}
        options={[{ value: 'grid', label: 'Grid' }, { value: 'list', label: 'List' }]}
      />
      <ToggleField label="Show excerpt" value={props.showExcerpt ?? true} onChange={v => onChange({ showExcerpt: v })} />
      <ToggleField label="Show cover image" value={props.showCover ?? true} onChange={v => onChange({ showCover: v })} />
    </div>
  )
}

// ─── Main panel ───────────────────────────────────────────────────────────────

const BLOCK_LABELS: Record<string, string> = {
  hero: 'Hero', text: 'Text', cta: 'Call to Action', features: 'Features',
  testimonials: 'Testimonials', pricing: 'Pricing', logos: 'Logos', stats: 'Stats',
  quote: 'Pull Quote', cards: 'Cards', image: 'Image', gallery: 'Gallery',
  spacer: 'Spacer', divider: 'Divider', video: 'Video', 'contact-form': 'Contact Form',
  embed: 'Embed', html: 'HTML', 'blog-feed': 'Blog Feed', columns: 'Columns', grid: 'Grid',
}

export function PropertiesPanel() {
  const { blocks, selectedBlockId, updateBlockProps, updateBlockSettings } = useEditorStore()
  const [tab, setTab] = useState<'content' | 'style'>('content')

  const selected = blocks.find(b => b.id === selectedBlockId)

  if (!selected) {
    return (
      <aside className="flex w-72 shrink-0 flex-col border-l border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Properties</p>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-xs text-gray-400">Select a block to edit</p>
        </div>
      </aside>
    )
  }

  const up = (p: object) => updateBlockProps(selected.id, p as never)
  const us = (s: Partial<BlockSettings>) => updateBlockSettings(selected.id, s)
  const p = selected.props as unknown

  const ContentEditor = () => {
    switch (selected.type) {
      case 'hero':         return <HeroEditor props={p as HeroProps} onChange={up} />
      case 'text':         return <TextEditor props={p as TextProps} onChange={up} />
      case 'cta':          return <CtaEditor props={p as CtaProps} onChange={up} />
      case 'features':     return <FeaturesEditor props={p as FeaturesProps} onChange={up} />
      case 'testimonials': return <TestimonialsEditor props={p as TestimonialsProps} onChange={up} />
      case 'pricing':      return <PricingEditor props={p as PricingProps} onChange={up} />
      case 'logos':        return <LogosEditor props={p as LogosProps} onChange={up} />
      case 'stats':        return <StatsEditor props={p as StatsProps} onChange={up} />
      case 'quote':        return <QuoteEditor props={p as QuoteProps} onChange={up} />
      case 'cards':        return <CardsEditor props={p as CardsProps} onChange={up} />
      case 'product-catalog': return <ProductCatalogEditor props={p as ProductCatalogProps} onChange={up} />
      case 'image':        return <ImageEditor props={p as ImageProps} onChange={up} />
      case 'gallery':      return <GalleryEditor props={p as GalleryProps} onChange={up} />
      case 'spacer':       return <SpacerEditor props={p as SpacerProps} onChange={up} />
      case 'video':         return <VideoEditor props={p as VideoProps} onChange={up} />
      case 'contact-form':  return <ContactFormEditor props={p as ContactFormProps} onChange={up} />
      case 'embed':         return <EmbedEditor props={p as EmbedProps} onChange={up} />
      case 'html':          return <HtmlEditor props={p as HtmlProps} onChange={up} />
      case 'blog-feed':     return <BlogFeedEditor props={p as BlogFeedProps} onChange={up} />
      case 'columns':       return <ColumnsEditor props={p as ColumnsProps} onChange={up} />
      case 'grid':          return <GridEditor props={p as GridProps} onChange={up} />
      case 'divider':       return <p className="text-xs text-gray-400">No content options for divider.</p>
      default:              return <p className="text-xs text-gray-400">No content options for this block type.</p>
    }
  }

  return (
    <aside className="flex w-72 shrink-0 flex-col border-l border-gray-200 bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {BLOCK_LABELS[selected.type] ?? selected.type}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {(['content', 'style'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-xs font-medium capitalize transition-colors ${
              tab === t ? 'border-b-2 border-violet-600 text-violet-700' : 'text-gray-400 hover:text-gray-700'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {tab === 'content' ? <ContentEditor /> : (
          <SettingsEditor settings={selected.settings} onChange={us} />
        )}
      </div>
    </aside>
  )
}
